/**
 * Select do design system: `<select>` nativo com a mesma pele do Input.
 *
 * Isto NÃO é um controle paralelo: reaproveita `inputVariants`, então altura,
 * borda, foco e alvo de toque são os mesmos do Input.
 *
 * `<select>` nativo de propósito: no celular ele abre a roda do sistema, que é
 * o melhor alvo de toque possível e sai de graça (mesma escolha já feita no
 * seletor de workspace da Topbar). O chevron é desenhado por cima, sem
 * capturar clique.
 */
import { forwardRef } from 'react';
import type { ReactNode, SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn';
import { inputVariants } from './Input';

export interface NativeSelectOption {
  value: string;
  label: string;
}

export interface NativeSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Atalho para listas simples; `children` continua valendo (e vem depois). */
  options?: Array<string | NativeSelectOption>;
  /** Classe do invólucro (largura/margem do bloco). `className` vai no `<select>`. */
  wrapperClassName?: string;
  children?: ReactNode;
}

export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(function NativeSelect(
  { className, wrapperClassName, invalid = false, size = 'md', options, children, ...props },
  ref,
) {
  return (
    <span className={cn('relative block w-full', wrapperClassName)}>
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(inputVariants({ size, invalid }), 'cursor-pointer pr-9', className)}
        {...props}
      >
        {options?.map((option) =>
          typeof option === 'string' ? (
            <option key={option} value={option}>
              {option}
            </option>
          ) : (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ),
        )}
        {children}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-subtle-foreground"
      />
    </span>
  );
});
